import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, shareReplay, switchMap} from 'rxjs';
import {NewsItem} from '../../model/news-item';
import {ProductChangeDto} from '../product-change/product-change-dto';
import {map} from 'rxjs/operators';
import {HttpParams, HttpResponse} from '@angular/common/http';
import {HttpClientService} from '../http-client/http-client.service';
import {EnvironmentService} from '../environment/environment.service';
import {NewsResponseDto} from './news-response-dto';
import {ProductChange} from '../../model/product-change';
import {NewsItemDto} from './news-item-dto';
import {Announcement} from '../../model/announcement';
import {AnnouncementDto} from './announcement-dto';
import {InformationScope} from '../../model/information-scope';
import {PersonalService} from '../personal/personal.service';
import {TypeGuards} from '../../utils/TypeGuards';
import {ProductChangeService} from '../product-change/product-change.service';


@Injectable({
  providedIn: 'root'
})
export class NewsService {

  newsItemsFilterAssociatedTemplate = 'filter-associated';
  private readonly newsItemsFilterTemplateSeparator = ',';

  private latestNewsItem?: [string, Observable<NewsItem>];

  private readonly newsItemsResource = this.environmentService.beUrl + 'news';
  newsItemsResourceTemplate = () : string => `${this.newsItemsResource}`;
  newsItemByIdResourceTemplate = (id: string ) : string => `${this.newsItemsResource}/${id}`;


  constructor(private readonly httpClientService: HttpClientService,
              private readonly environmentService: EnvironmentService,
              private readonly personal: PersonalService) { }

  private mapNewsResponse(newsResponseDto: NewsResponseDto): NewsItem[] {
    if (null === newsResponseDto) {
      return new Object(null) as NewsItem[];
    }
    const newsItems: NewsItem[] = [];
    if  (null !== newsResponseDto.news) {
      newsResponseDto.news.forEach(newsItemDto => {
        newsItems.push(this.mapNewsItem(newsItemDto));
      });
    }

    return newsItems;
  }

  private mapNewsItem(newsItemDto: NewsItemDto): NewsItem {
    if (null === newsItemDto) {
      return new Object(null) as NewsItem;
    }
    let detail: Announcement | ProductChange;
    switch (newsItemDto.scope) {
      case InformationScope.Release:
        detail = ProductChangeService.mapProductChange(newsItemDto.detail as ProductChangeDto);
        break;
      case InformationScope.Announcement:
        detail = this.mapAnnouncement(newsItemDto.detail as AnnouncementDto);
        break;
      default:
        detail = new Object(null) as Announcement;
        break;
    }
    return {
      id: newsItemDto.id,
      scope: newsItemDto.scope,
      detail
    };
  }

  private mapAnnouncement(announcementDto: AnnouncementDto): Announcement {
    if (null === announcementDto ||
        !TypeGuards.isNonEmptyString(announcementDto.id) ||
        !TypeGuards.isNonEmptyString(announcementDto.title) ||
        !TypeGuards.isNonEmptyString(announcementDto.publishing_date) ||
        !TypeGuards.isDate(new Date(announcementDto.publishing_date))) {
      return new Object(null) as Announcement;
    }

    return {
      id: announcementDto.id,
      title: announcementDto.title,
      teaser: announcementDto.teaser ?? [],
      documentId: announcementDto.document_id ?? '',
      publishingDate: new Date(announcementDto.publishing_date)
    };
  }

  getNewsItems(businessPartnerFilterIsActive: boolean = false): Observable<NewsItem[]> {
    if (businessPartnerFilterIsActive) {
      return this.personal.activeBusinessPartner$.pipe(
        switchMap(businessPartner => this.personal.getBusinessPartnerFilterHKNRs(businessPartner.businessPartnerId)),
        switchMap(filterHKNRs => {

          let httpParams = new HttpParams();
          httpParams = httpParams.append(this.newsItemsFilterAssociatedTemplate,
            filterHKNRs.join(this.newsItemsFilterTemplateSeparator));
          return this.httpClientService.get<NewsResponseDto>(`${this.newsItemsResource}?${httpParams.toString()}`)
            .pipe(
              map((resp: HttpResponse<NewsResponseDto>) => this.mapNewsResponse(resp.body as NewsResponseDto))
            );
        })
      );

    } else {
      return this.httpClientService.get<NewsResponseDto>(this.newsItemsResource)
        .pipe(
          map((resp: HttpResponse<NewsResponseDto>) => this.mapNewsResponse(resp.body as NewsResponseDto)),
          shareReplay(1)
        );
    }
  }

  getNewsItemById(id: string): Observable<NewsItem> {
    if (!this.latestNewsItem || this.latestNewsItem[0] !== id) {
      const newsItemObservable = this.httpClientService.get<NewsItemDto>(this.newsItemByIdResourceTemplate(id))
        .pipe(
          map((resp: HttpResponse<NewsItemDto>) => this.mapNewsItem(resp.body as NewsItemDto)),
          shareReplay(1));
      this.latestNewsItem = [id, newsItemObservable];
    }
    return this.latestNewsItem[1];
  }

  setLatestNewsItem(newsItem: NewsItem): void {
    const newsItem$ = new BehaviorSubject<NewsItem>(newsItem);
    this.latestNewsItem = [newsItem.id, newsItem$];
  }

}
